import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import "./StreamsRuns.css";
import { ReactComponent as LeftArrow } from "../images/LeftArrow.svg";
import { ReactComponent as RightArrow } from "../images/RightArrow.svg";
import iHubLogo from "../images/ILH-HORIZONTAL-FULL-COLOR.jpg";
import HomeIcon from "../images/HomeIcon.svg";
import config from "../config/config";

const StreamsRuns = ({ label }) => {
  const { streamId, period } = useParams(); // Extract Stream ID and Period from URL
  const [streamDetails, setStreamDetails] = useState(null); // Stream details
  const [runsData, setRunsData] = useState([]); // Stream Runs data
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state
  const [currentPage, setCurrentPage] = useState(1); // Pagination
  const rowsPerPage = 8; // Rows per page
  const navigate = useNavigate(); // For navigation to Stream Events page
  const [showErrorsOnly, setShowErrorsOnly] = useState(() => {
    return sessionStorage.getItem("showErrorsOnly") === "true";
  });

  const handleShowErrorsOnlyChange = () => {
    const newValue = !showErrorsOnly;
    setShowErrorsOnly(newValue);
    sessionStorage.setItem("showErrorsOnly", newValue);
  };

  useEffect(() => {
    // Fetch stream details and runs data
    const fetchStreamDetailsAndRuns = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch all streams from the endpoint
        const streamsResponse = await axios.get(
          `${config.API_BASE_URL}/default/Streams/?ProjectCode=AFS&Period1=Week&Period2=Day`
        );

        // Filter the specific stream using Stream ID
        const filteredStream = streamsResponse.data.find(
          (stream) => stream.Id === streamId
        );

        if (!filteredStream) {
          throw new Error("Stream not found for the provided ID.");
        }

        setStreamDetails(filteredStream); // Set the stream details

        // Fetch runs data for the stream
        const runsResponse = await axios.get(
          `${config.API_BASE_URL}/default/Runs/ByPeriod/?StreamId=${streamId}&Period=${period}`
        );
        setRunsData(runsResponse.data || []);
      } catch (err) {
        setError(err.message || "Failed to fetch data. Please try again.");
        console.error("Error fetching stream data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchStreamDetailsAndRuns();
  }, [streamId, period]); // Re-fetch if StreamId or Period changes

  // Pagination Logic
  useEffect(() => {
    // Load the saved page from sessionStorage if available
    const savedPage = sessionStorage.getItem(
      `streamsRunsPage_${streamId}_${period}`
    );
    if (savedPage) {
      setCurrentPage(Number(savedPage)); // Set current page from sessionStorage
    } else {
      setCurrentPage(1); // Default to page 1 if no saved page
    }
  }, [streamId, period]); // Re-run when StreamId or Period changes

  const filteredRuns = showErrorsOnly
    ? runsData.filter((run) => run.Status === "E")
    : runsData;

  // Calculate pagination indices
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const totalPages = Math.ceil(filteredRuns.length / rowsPerPage);
  const currentRows = filteredRuns.slice(indexOfFirstRow, indexOfLastRow);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
      sessionStorage.setItem(`streamsRunsPage_${streamId}_${period}`, newPage); // Save page to sessionStorage
    }
  };

  // Handle row click to navigate to Stream Events
  const handleRowClick = (runId) => {
    navigate(`/stream-events/${runId}`, {
      state: { StreamName: streamDetails?.StreamName },
    });
  };

  // Helper Function: Format Date | Time
  const formatDateTime = (dateTime) => {
    if (!dateTime) return "N/A";
    const date = new Date(dateTime);
    const formattedDate = date.toLocaleDateString(); // Format as MM/DD/YYYY or based on locale
    const formattedTime = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }); // Format as HH:MM AM/PM
    return `${formattedDate} | ${formattedTime}`;
  };

  return (
    <div className="streamRuns-lay">
      <div className="streamRuns-layout">
        <div className="streamsRuns-header">
          <div className="streamsRuns-logo">
            <img src={iHubLogo} alt="iHubLogo" />
          </div>
          <h1 className="streamRuns-title">Stream Runs</h1>
          <Link to="/">
            <div className="streamRuns-homeButton">
              <img src={HomeIcon} alt="Home Icon" />
              <p class="home-btn-text">Home</p>
            </div>
          </Link>
        </div>

        <div className="streamsRuns-infoContainer">
          <div className="streamRuns-filters">
            <div className="filter-group">
              <div className="streamRuns-filterGroup">
                <label htmlFor="stream-name" className="streamRuns-filterLabel">
                  Stream Name
                </label>
                <div className="streamRuns-dataField">
                  <p className="streamRuns-dataInfo">
                    {streamDetails?.StreamName || ""}
                  </p>
                </div>
              </div>
            </div>

            <div className="filter-group">
              <div className="streamRuns-filterGroup">
                <label
                  htmlFor="trigger-type"
                  className="streamRuns-filterLabel"
                >
                  Trigger Type
                </label>
                <div className="streamRuns-dataField1">
                  <p className="streamRuns-dataInfo">
                    {streamDetails?.TriggerType || ""}
                  </p>
                </div>
              </div>
              <div className="streamRuns-filterGroup">
                <label htmlFor="trigger" className="streamRuns-filterLabel">
                  Trigger
                </label>
                <div className="streamRuns-dataField1">
                  <p className="streamRuns-dataInfo">
                    {streamDetails?.TriggerDesc || ""}
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* Show Errors Only */}
          <div
            className="streamRuns-errorFilter"
            style={{ marginTop: "-40px", marginBottom: "18px" }}
          >
            <label className="custom-checkbox">
              <input
                type="checkbox"
                checked={showErrorsOnly}
                onChange={handleShowErrorsOnlyChange}
                className="custom-checkbox-input"
              />

              <span className="custom-checkbox-box"></span>
              <p className="custom-checkbox-text">Show Only Errors</p>
              {label && <span className="custom-checkbox-label">{label}</span>}
            </label>
          </div>
          {/* Table Section */}
          <div className="streamRuns-tableContainer">
            {loading ? (
              <div className="streamPage-loading">
                <div className="spinner"></div>
                Loading Data...
              </div>
            ) : error ? (
              <p className="error-message">{error}</p>
            ) : (
              <table className="streamRuns-table">
                <thead>
                  <tr>
                    <th>Filename</th>
                    <th>Filesize</th>
                    <th>Start</th>
                    <th>Duration</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {currentRows.length > 0 ? (
                    currentRows.map((run, index) => (
                      <tr
                        key={run.RunId || index} // Fallback to index if RunId is not unique or undefined
                        onClick={() => handleRowClick(run.RunId)}
                        style={{ cursor: "pointer" }}
                      >
                        <td>{run.Filename}</td>
                        <td>{run.FileSize} MB</td>
                        <td>{formatDateTime(run.StartTime)}</td>
                        <td>{run.Duration || "N/A"} s</td>
                        <td>{run.Status || "N/A"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="no-data-message">
                        No Stream Runs available for this period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
          {/* Pagination Section */}
          {!loading && totalPages > 1 && (
            <div className="streamRuns-pagination">
              <button
                className="streamRuns-paginationButtonArrow"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <LeftArrow className="streamsPage-arrowIcon" />
              </button>

              {/* Create an array of page numbers based on currentPage */}
              {Array.from({ length: Math.min(5, totalPages) }).map(
                (_, index) => {
                  let pageNumber;

                  // If we are on the first 3 pages, show the first 5 pages
                  if (currentPage <= 3) {
                    pageNumber = index + 1;
                  }
                  // If we are near the last page, show the last 5 pages
                  else if (currentPage >= totalPages - 2) {
                    pageNumber = totalPages - 5 + index;
                  }
                  // Otherwise, show a range centered around the current page
                  else {
                    pageNumber = currentPage - 2 + index;
                  }

                  // Skip pages that are out of bounds
                  if (pageNumber < 1 || pageNumber > totalPages) {
                    return null;
                  }

                  return (
                    <button
                      key={pageNumber}
                      className={`streamRuns-paginationButton ${
                        currentPage === pageNumber ? "active" : ""
                      }`}
                      onClick={() => handlePageChange(pageNumber)}
                    >
                      {pageNumber}
                    </button>
                  );
                }
              )}

              <button
                className="streamRuns-paginationButtonArrow"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <RightArrow className="streamRuns-arrowIcon" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StreamsRuns;
